import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { BooksReadStudentData } from 'teacher-ui-client/components/giraffe/charts/books-read/student'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface BooksReadStudentResponse {
  books_read_events_summary: SummaryData
  books_read_events: EventData[]
}

interface SummaryData {
  data: [
    { category: 'total_count'; count: number },
    { category: 'average_lexile_level'; count: Nullable<number> },
    { category: 'average_lexile_formatted'; count: Nullable<string> },
  ]
}

interface EventData {
  category: 'library' | 'my_program' | 'CompleteBookChapterQuiz' | 'CompleteBook'
  event_type: string
  precinct: string
  book_name: string
  created_at: number
  lexile: number
  lexile_code: string
  lexile_formatted: string
}

export async function booksReadStudent(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<BooksReadStudentData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('books_read_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<BooksReadStudentResponse>(url, query)
  if (!data) return null

  const { books_read_events_summary, books_read_events } = data
  if (books_read_events.length === 0) return null

  const total_count = books_read_events_summary.data.find((d) => d.category === 'total_count')!.count
  const average_lexile_level = books_read_events_summary.data.find((d) => d.category === 'average_lexile_level')!.count
  const average_lexile_formatted = books_read_events_summary.data.find(
    (d) => d.category === 'average_lexile_formatted',
  )!.count
  const filteredEvents = books_read_events.map((event) => ({
    category: event.category,
    book_name: event.book_name,
    created_at: event.created_at,
    lexile: event.lexile,
    lexile_formatted: event.lexile_formatted,
  }))

  return {
    summary: {
      total_count,
      average_lexile_level,
      average_lexile_formatted,
    },
    events: filteredEvents,
  }
}
